import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import TextField from "@material-ui/core/TextField";
import SearchIcon from "@material-ui/icons/Search";
import theme from "../../../../Themes/defaultTheme";
import { handleOptionSetValue } from "../../../../../Utils/Helpers/optionSet.helper";
import {
  customDateFormat,
  FromEpochFormat,
} from "../../../../../Utils/Helpers/dateUtils";
import A_FormHelper from "../../DataDisplay/Tooltips/A_FormHelper";
import { InputAdornment } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";

const useStyles = makeStyles(() => ({
  clearButton: {
    padding: 2,
    marginRight: 18,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    "&:hover": {
      color: "var(--color-text-primary)",
    },
  },
  formControl: {
    width: "222px",
    marginTop: 5,
    "& .MuiInputLabel-root:not(.Mui-error)": {
      color: "var(--color-text-secondary)",
      fontSize: "0.8125rem",
      "&.Mui-focused": {
        color: "var(--color-primary-text)",
      },
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      backgroundColor: "var(--color-background-paper)",
      paddingLeft: 4,
      paddingRight: 4,
    },
    "& .MuiInputBase-root": {
      color: "var(--color-text-primary) !important",
      "&::before": {
        borderBottomColor: "var(--total_row_boldbackground)",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "var(--color-primary-main)",
      },
      "&::after": {
        borderBottomColor: "var(--color-primary-main)",
      },
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "var(--color-background-paper)",
      borderRadius: "calc(8px * var(--app-radius-scale, 1))",
      "& fieldset": {
        borderColor: "var(--total_row_boldbackground)",
        borderRadius: "inherit",
      },
      "&:hover fieldset": {
        borderColor: "var(--color-primary-main)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--color-primary-main)",
      },
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "var(--total_row_boldbackground)",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& .MuiFormHelperText-root:not(.Mui-error)": {
      color: "var(--color-text-secondary)",
    },
  },
  selectLabel: {
    marginTop: "-1.8px",
  },
  fullWidth: {
    width: "100%",
  },
  select: {
    fontSize: "0.8125rem",
    height: "auto",
    minHeight: "1.1876em",
    "& .MuiSelect-icon": {
      color: "var(--color-text-secondary)",
      'html[data-mode="dark"] &': {
        color: "var(--color-text-primary) !important",
      },
    },
    "& .MuiSelect-select": {
      color: "var(--color-text-primary)",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
      paddingTop: "9px",
      paddingBottom: "9px",
      minHeight: "1.1876em",
      fontSize: "0.8125rem",
      display: "flex",
      alignItems: "center",
    },
  },
  selectedText: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    fontSize: "0.8125rem",
  },
  minWidth: {
    width: "180px",
  },
  helperIcon: {
    "& svg": {
      marginRight: "20px",
    },
    "& .helperIcon": {
      marginRight: "0px",
    },
  },
  menuPaper: {
    marginTop: "4px !important",
    backgroundColor: "var(--color-background-paper) !important",
    color: "var(--color-text-primary) !important",
    borderRadius: "calc(8px * var(--app-radius-scale, 1)) !important",
    boxShadow: "0 8px 16px rgba(0,0,0,0.4) !important",
    border: "1px solid var(--total_row_boldbackground)",
    boxSizing: "border-box",
    overflowX: "hidden",
    "& .MuiMenuItem-root": {
      fontSize: "0.8125rem",
      padding: "7px 14px",
      minHeight: "36px",
      boxSizing: "border-box",
      borderBottom:
        "1px solid var(--color-divider, var(--total_row_boldbackground, rgba(128, 128, 128, 0.15)))",
      "&:last-child": {
        borderBottom: "none",
      },
      whiteSpace: "normal",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      color: "var(--color-text-primary) !important",
      transition: "background-color 0.2s ease, color 0.2s ease",
      "&:hover": {
        backgroundColor: "var(--total_row_background) !important",
        color: "var(--color-primary-text) !important",
      },
      "&.Mui-selected": {
        backgroundColor: "var(--total_row_boldbackground) !important",
        color: "var(--color-primary-text) !important",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "var(--total_row_boldbackground) !important",
          opacity: 0.9,
        },
      },
    },
    "& .MuiListSubheader-root": {
      color: "var(--color-text-secondary) !important",
      backgroundColor: "var(--color-background-paper) !important",
      lineHeight: "36px",
      fontWeight: 700,
      fontSize: "0.6875rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  },
  menuItemText: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    fontSize: "0.8125rem",
    lineHeight: 1.35,
    width: "100%",
  },
  searchBox: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    listStyle: "none",
    padding: 0,
    backgroundColor: "var(--color-background-paper)",
    borderBottom: "1px solid var(--total_row_boldbackground)",
  },
  searchBoxInner: {
    padding: "8px 10px",
  },
  searchField: {
    margin: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: "calc(8px * var(--app-radius-scale, 1))",
      backgroundColor: "var(--color-background-default)",
      "& fieldset": { borderColor: "var(--total_row_boldbackground)" },
      "&:hover fieldset": { borderColor: "var(--color-primary-main)" },
      "&.Mui-focused fieldset": { borderColor: "var(--color-primary-main)" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 6px",
      fontSize: "0.8125rem",
      color: "var(--color-text-primary)",
    },
    "& .MuiSvgIcon-root": { color: "var(--color-text-secondary)" },
  },
  noResults: {
    listStyle: "none",
    padding: "12px 16px",
    fontSize: "0.8125rem",
    color: "var(--color-text-secondary)",
    textAlign: "center",
  },
}));

const SelectedValueWithTooltip = ({
  fullText,
  children,
  className,
  isMenuOpen,
  enterDelay = 1000,
}) => {
  const textRef = React.useRef(null);
  const [isOverflowed, setIsOverflowed] = React.useState(false);

  const checkOverflow = () => {
    if (textRef.current) {
      const hasOverflow =
        textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsOverflowed(hasOverflow);
    }
  };

  React.useEffect(() => {
    checkOverflow();
  }, [fullText]);

  return (
    <Tooltip
      key={isMenuOpen ? "open" : "closed"}
      title={isOverflowed && !isMenuOpen ? fullText : ""}
      disableHoverListener={!isOverflowed || isMenuOpen}
      disableFocusListener={true}
      disableTouchListener={true}
      arrow
      placement="top"
      enterDelay={enterDelay}
    >
      <span ref={textRef} onMouseEnter={checkOverflow} className={className}>
        {children}
      </span>
    </Tooltip>
  );
};

SelectedValueWithTooltip.propTypes = {
  fullText: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.any,
  isMenuOpen: PropTypes.bool,
  enterDelay: PropTypes.number,
};

const A_Select = (props) => {
  const classes = useStyles();
  const formControlRef = React.useRef(null);
  const selectRootRef = React.useRef(null);
  const [menuWidth, setMenuWidth] = React.useState(null);

  const currentValue =
    props.value !== undefined && props.value !== ""
      ? props.value
      : props.defaultValue;
  const [value, setValue] = React.useState(
    handleOptionSetValue(currentValue, false, true)
  );

  React.useEffect(() => {
    const val =
      props.value !== undefined && props.value !== ""
        ? props.value
        : props.defaultValue;
    setValue(handleOptionSetValue(val, false, true));
  }, [props.value, props.defaultValue]);

  const [searchText, setSearchText] = React.useState("");
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleOpen = (event) => {
    const el = selectRootRef.current || formControlRef.current;
    if (el && typeof el.getBoundingClientRect === "function") {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) {
        setMenuWidth(Math.round(rect.width));
      }
    }
    setMenuOpen(true);
    if (props.onOpen) props.onOpen(event);
  };

  const handleClose = (event) => {
    setMenuOpen(false);
    if (props.searchable) setSearchText("");
    if (props.onClose) props.onClose(event);
  };

  const getOptionLabelForValue = (val) => {
    if (val === undefined || val === null || val === "") return "";
    if (props.options && Array.isArray(props.options)) {
      const matched = props.options.find(
        (opt) => handleOptionSetValue(opt.value, false) == val
      );
      if (matched) return handleOptionSetValue(matched.label);
    }
    if (props.groupedOptions && Array.isArray(props.groupedOptions)) {
      for (const group of props.groupedOptions) {
        const matched = (group.options || []).find(
          (opt) => handleOptionSetValue(opt.value, false) == val
        );
        if (matched) return handleOptionSetValue(matched.label);
      }
    }
    return val;
  };

  const renderSelectValue = (selectedVal) => {
    let displayContent;
    if (props.renderValue) {
      displayContent = props.renderValue(selectedVal);
    } else {
      displayContent = getOptionLabelForValue(selectedVal);
    }

    if (!displayContent && displayContent !== 0) {
      return "";
    }

    const fullText =
      typeof displayContent === "string" || typeof displayContent === "number"
        ? String(displayContent)
        : displayContent;

    return (
      <SelectedValueWithTooltip
        fullText={fullText}
        className={classes.selectedText}
        isMenuOpen={menuOpen}
        enterDelay={props.enterDelay !== undefined ? props.enterDelay : 1000}
      >
        {displayContent}
      </SelectedValueWithTooltip>
    );
  };

  const searchTerm = (searchText || "").trim().toLowerCase();
  const selectValue = props.isCascading
    ? handleOptionSetValue(props.defaultValue, false, true)
    : value && handleOptionSetValue(value);
  const handleChange = (event) => {
    if (event.target.value !== "") {
      let selectedOption = props.options.filter(function (option) {
        return handleOptionSetValue(option.value, false) == event.target.value;
      })[0];
      setValue(selectedOption.value);
      props.onChange(selectedOption, props.id, false);
    } else {
      setValue(event.target.value);
      props.onChange({ value: event.target.value }, props.id);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setValue("");
    if (props.onChange) {
      props.onChange({ value: "", label: "" }, props.id);
    }
  };

  const filteredOptionsList =
    props.searchable && searchTerm
      ? props.options.filter(
        (option) =>
          `${handleOptionSetValue(option.label)}`
            .toLowerCase()
            .includes(searchTerm) ||
          handleOptionSetValue(option.value, false) === selectValue
      )
      : props.options;

  let options = filteredOptionsList.map((option) => (
    <MenuItem
      key={handleOptionSetValue(option.value, false)}
      value={handleOptionSetValue(option.value, false)}
    >
      <span
        className={classes.menuItemText}
        title={
          typeof option.label === "string"
            ? option.label
            : String(handleOptionSetValue(option.label) || "")
        }
      >
        {handleOptionSetValue(option.label)}
      </span>
    </MenuItem>
  ));

  if (
    props.label === "Date" &&
    typeof props.defaultValue == "number" &&
    `${props.defaultValue}`.length >= 10 &&
    options.every((option) => {
      return !customDateFormat(
        FromEpochFormat(props.defaultValue),
        "MMMM YYYY"
      ).includes(option.props.children);
    })
  ) {
    let dateOption = customDateFormat(
      FromEpochFormat(props.defaultValue),
      "MMMM YYYY"
    );
    dateOption &&
      options.push(
        <MenuItem
          key={handleOptionSetValue(dateOption, false, true)}
          value={handleOptionSetValue(dateOption, false, true)}
        >
          <span
            className={classes.menuItemText}
            title={String(dateOption)}
          >
            {handleOptionSetValue(dateOption, false, true)}
          </span>
        </MenuItem>
      );
  }

  let showGroupedOptions = () => {
    let optionsList = [];
    props.groupedOptions.forEach((groupedOption) => {
      const groupItems = (groupedOption.options || []).filter((option) =>
        props.searchable && searchTerm
          ? `${handleOptionSetValue(option.label)}`
            .toLowerCase()
            .includes(searchTerm) ||
          handleOptionSetValue(option.value, false) === selectValue
          : true
      );
      if (props.searchable && searchTerm && groupItems.length === 0) return;
      optionsList.push(
        <ListSubheader key={groupedOption.title} disableSticky>
          {groupedOption.title}
        </ListSubheader>
      );
      groupItems.forEach((option) => {
        optionsList.push(
          <MenuItem
            key={handleOptionSetValue(option.value, false)}
            value={handleOptionSetValue(option.value, false)}
          >
            <span
              className={classes.menuItemText}
              title={
                typeof option.label === "string"
                  ? option.label
                  : String(handleOptionSetValue(option.label) || "")
              }
            >
              {handleOptionSetValue(option.label)}
            </span>
          </MenuItem>
        );
      });
    });
    return optionsList;
  };
  return (
    <ThemeProvider theme={theme}>
      <FormControl
        ref={formControlRef}
        error={props.error}
        key={"formcontrol" + props.id}
        id={"formcontrol" + props.id}
        variant={props.variant}
        className={`${props.className ? props.className : classes.formControl
          } ${props.fullWidth ? classes.fullWidth : ""} ${props.minWidth ? classes.minWidth : ""
          }`}
        required={props.required}
        style={props.style}
      >
        {props.label ? (
          <InputLabel
            className={classes.selectLabel}
            key={props.id + "_label"}
            id={props.id + "_label"}
            htmlFor={props.id}
            {...(props.InputLabelProps || {})}
          >
            {props.label}
          </InputLabel>
        ) : null}
        <Select
          ref={selectRootRef}
          key={props.id}
          id={props.id}
          label={
            props.variant === "outlined" && props.label
              ? props.label
              : undefined
          }
          labelId={props.label ? props.id + "_label" : undefined}
          value={
            props.isCascading
              ? handleOptionSetValue(props.defaultValue, false, true)
              : value && handleOptionSetValue(value)
          }
          native={false}
          style={props.style}
          renderValue={renderSelectValue}
          //variant={props.variant}
          endAdornment={
            <InputAdornment position="end">
              {props.clearable && Boolean(selectValue) && !props.disabled ? (
                <IconButton
                  size="small"
                  aria-label={`Clear ${props.label || "selection"}`}
                  onClick={handleClear}
                  className={classes.clearButton}
                >
                  <CloseIcon fontSize="small" style={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
              {props.fieldHelperText ? (
                <A_FormHelper
                  openOptionModal={props.openOptionModal}
                  id={props.id}
                  fieldHelperText={props.fieldHelperText}
                />
              ) : null}
            </InputAdornment>
          }
          disabled={props.disabled}
          required={props.required}
          autoWidth={props.autoWidth}
          displayEmpty={props.displayEmpty}
          inputProps={{
            ...props.inputProps,
            name: props.id,
            id: props.id,
            readOnly: props.readOnly,
            style:
              props.inputProps && props.inputProps != ""
                ? props.inputProps
                : {},
          }}
          color={props.color}
          className={`${props.className} ${classes.select} ${props.fieldHelperText ? classes.helperIcon : ""
            }`}
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          onKeyDown={(e) => e.stopPropagation()}
          MenuProps={{
            getContentAnchorEl: null,
            anchorEl: () => selectRootRef.current || formControlRef.current,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            ...(props.searchable
              ? {
                autoFocus: false,
                variant: "menu",
                MenuListProps: { disablePadding: true },
              }
              : {}),
            ...(props.MenuProps || {}),
            classes: {
              paper: classes.menuPaper,
              ...((props.MenuProps && props.MenuProps.classes) || {}),
            },
            PaperProps: {
              ...((props.MenuProps && props.MenuProps.PaperProps) || {}),
              style: {
                marginTop: "4px",
                ...(menuWidth && menuWidth > 0
                  ? {
                    width: `${menuWidth}px`,
                    maxWidth: `${menuWidth}px`,
                    minWidth: `${menuWidth}px`,
                  }
                  : {}),
                boxSizing: "border-box",
                ...(props.searchable ? { maxHeight: props.maxMenuHeight } : {}),
                ...((props.MenuProps &&
                  props.MenuProps.PaperProps &&
                  props.MenuProps.PaperProps.style) ||
                  {}),
              },
            },
          }}
        >
          {props.searchable ? (
            <li
              key="__a_select_search"
              className={classes.searchBox}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={classes.searchBoxInner}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") e.stopPropagation();
                }}
              >
                <TextField
                  autoFocus
                  fullWidth
                  variant="outlined"
                  placeholder={props.searchPlaceholder}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </li>
          ) : null}
          {props.groupedOptions ? showGroupedOptions() : options}
          {!props.groupedOptions &&
            props.searchable &&
            Boolean(searchTerm) &&
            filteredOptionsList.length === 0 ? (
            <li key="__a_select_noresults" className={classes.noResults}>
              No matches found
            </li>
          ) : null}
        </Select>
        {props.helperText ? (
          <FormHelperText
            key={"helptext" + props.id}
            id={props.helperTextId || `${props.id}-helper-text`}
          >
            {props.helperText}
          </FormHelperText>
        ) : null}
      </FormControl>
    </ThemeProvider>
  );
};
A_Select.defaultProps = {
  id: "xxxx",
  //label: "Select",
  value: "",
  variant: "outlined",
  color: "primary",
  inputProps: "",
  disabled: false,
  autoWidth: false,
  displayEmpty: true,
  error: false,
  required: false,
  options: [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ],
  native: false,
  style: {},
  fieldHelperText: "",
  isCascading: false,
  searchable: false,
  maxMenuHeight: 320,
  searchPlaceholder: "Search...",
  clearable: false,
};
A_Select.propTypes = {
  variant: PropTypes.oneOf(["filled", "outlined", "standard"]),
  color: PropTypes.oneOf(["default", "inherit", "primary", "secondary"]),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  autoWidth: PropTypes.bool,
  displayEmpty: PropTypes.bool,
  onClick: PropTypes.func,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  helperText: PropTypes.string,
  className: PropTypes.any,
  id: PropTypes.any,
  label: PropTypes.string,
  options: PropTypes.array,
  native: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.any,
  groupedOptions: PropTypes.any,
  error: PropTypes.any,
  style: PropTypes.any,
  fullWidth: PropTypes.any,
  minWidth: PropTypes.any,
  optionSet: PropTypes.any,
  inputProps: PropTypes.any,
  fieldHelperText: PropTypes.string,
  isCascading: PropTypes.bool,
  InputLabelProps: PropTypes.object,
  searchable: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  searchPlaceholder: PropTypes.string,
  renderValue: PropTypes.func,
  clearable: PropTypes.bool,
  value: PropTypes.any,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  enterDelay: PropTypes.number,
  openOptionModal: PropTypes.func,
  helperTextId: PropTypes.string,
  MenuProps: PropTypes.shape({
    classes: PropTypes.object,
    PaperProps: PropTypes.shape({
      style: PropTypes.object,
    }),
  }),
};
export default A_Select;
